<template>
  <div class="media-file-card">
    <!-- Loading state -->
    <div v-if="loading" class="media-file-loading">
      <i class="el-icon-loading"></i>
      <span>{{ $t('workbench.loading') }}</span>
    </div>
    <!-- Error state -->
    <div v-else-if="error" class="media-file-error" @click="retry">
      <i class="el-icon-warning-outline"></i>
      <span class="error-text">{{ $t('workbench.loadFailed') }}</span>
      <span class="retry-btn">{{ $t('workbench.retry') }}</span>
    </div>
    <!-- Download card (matches user attachment file style) -->
    <a
      v-else-if="downloadUrl"
      :href="downloadUrl"
      class="media-file-download"
      :download="fileName"
      target="_blank"
      rel="noopener noreferrer"
    >
      <div class="media-file-type-badge" :style="{ backgroundColor: getFileTypeColor(fileName) }">
        <span>{{ getFileTypeLabel(fileName) }}</span>
      </div>
      <div class="media-file-name" :title="fileName">{{ fileName }}</div>
      <i class="el-icon-download"></i>
    </a>
  </div>
</template>

<script>
/**
 * MediaFileCard — renders a downloadable file card from a MEDIA:<path> reference.
 *
 * Flow:
 * 1. Mount → fetch /__openclaw__/assistant-media?meta=1&source=<path>
 *    with Authorization: Bearer <gateway-token>
 * 2. Response returns a mediaTicket → construct download URL
 * 3. Render as <a href> matching the user attachment file card style
 */
export default {
  name: 'MediaFileCard',
  props: {
    source: { type: String, required: true }
  },
  data() {
    return {
      loading: true,
      error: false,
      downloadUrl: ''
    }
  },
  computed: {
    fileName() {
      if (!this.source) return ''
      return this.source.split('/').pop() || this.source
    }
  },
  mounted() {
    this._fetchMediaTicket()
  },
  watch: {
    /**
     * 流式输出时 source 可能逐步拼完整（"MEDIA:/path/fil" → "/path/file.pdf"），
     * 每次变更需重新获取 mediaTicket
     */
    source: {
      immediate: false,
      handler() {
        this._fetchMediaTicket()
      }
    }
  },
  methods: {
    _getAgentConfig() {
      const store = this.$store.state.cosmoClaw
      const agentName = store.currentAgentName
      const agentId = store.currentAgentId
      const ad = agentId && store.agentData[agentId]
      const token = ad?.gatewayToken || store.token
      const gatewayUrl = ad?.gatewayUrl || store.accessUrl
      return { token, gatewayUrl, agentName }
    },
    async _fetchMediaTicket() {
      // 流式场景下 source 可能逐步更新，记录当前值用于后续校验
      const currentSource = this.source
      this.loading = true
      this.error = false
      try {
        const { token, gatewayUrl } = this._getAgentConfig()
        if (!token) {
          console.warn('[MediaFileCard] no gateway token available')
          this.error = true
          this.loading = false
          return
        }
        // Use the proxy path: /__openclaw__/{host:port}/assistant-media?...
        // The dev-server proxy routes this to the correct gateway IP:port
        const proxyPrefix = gatewayUrl ? `/__openclaw__/${gatewayUrl}` : ''
        const metaUrl = `${proxyPrefix}/assistant-media?meta=1&source=${encodeURIComponent(this.source)}`
        const resp = await fetch(metaUrl, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
        const data = await resp.json()
        // Response can be { mediaTicket: 'xxx' } or { data: { mediaTicket: 'xxx' } }
        const ticket = data.mediaTicket || (data.data && data.data.mediaTicket)
        if (!ticket) throw new Error('no mediaTicket in response')
        // 如果 fetch 期间 source 已更新，丢弃本次结果
        if (this.source !== currentSource) return
        this.downloadUrl = `${proxyPrefix}/assistant-media?source=${encodeURIComponent(this.source)}&mediaTicket=${encodeURIComponent(ticket)}`
        this.loading = false
      } catch (e) {
        // fetch 期间 source 已更新 → 不报错，让下一次 fetch 处理
        if (this.source !== currentSource) return
        console.error('[MediaFileCard] fetch failed:', e)
        this.error = true
        this.loading = false
      }
    },
    retry() {
      this._fetchMediaTicket()
    },
    _getExtension(name) {
      if (!name) return ''
      const parts = name.split('.')
      return parts.length > 1 ? parts.pop().toLowerCase() : ''
    },
    getFileTypeColor(name) {
      const ext = this._getExtension(name)
      const colorMap = {
        pdf: '#E53935', doc: '#1E88E5', docx: '#1E88E5',
        xls: '#43A047', xlsx: '#43A047', csv: '#26A69A',
        ppt: '#FB8C00', pptx: '#FB8C00',
        js: '#7E57C2', ts: '#7E57C2', py: '#7E57C2', java: '#7E57C2',
        txt: '#78909C', md: '#78909C',
        zip: '#8D6E63', rar: '#8D6E63', '7z': '#8D6E63',
        mp4: '#AB47BC', avi: '#AB47BC', mov: '#AB47BC',
        mp3: '#5C6BC0', wav: '#5C6BC0'
      }
      return colorMap[ext] || '#90A4AE'
    },
    getFileTypeLabel(name) {
      const ext = this._getExtension(name)
      const labelMap = {
        pdf: 'PDF', doc: 'DOC', docx: 'DOC',
        xls: 'XLS', xlsx: 'XLS', csv: 'CSV',
        ppt: 'PPT', pptx: 'PPT',
        js: 'CODE', ts: 'CODE', py: 'CODE', java: 'CODE',
        txt: 'TXT', md: 'TXT',
        zip: 'ZIP', rar: 'RAR', '7z': '7Z',
        mp4: 'MP4', avi: 'AVI', mov: 'MOV',
        mp3: 'MP3', wav: 'WAV'
      }
      return labelMap[ext] || ext.toUpperCase().slice(0, 4) || 'FILE'
    }
  }
}
</script>

<style scoped>
.media-file-card {
  margin: 8px 0;
}
.media-file-loading {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #FAFAFA;
  border-radius: 8px;
  font-size: 13px;
  color: #8f959e;
  width: fit-content;
}
.media-file-error {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #fff2f0;
  border-radius: 8px;
  font-size: 13px;
  color: #f5222d;
  cursor: pointer;
  width: fit-content;
}
.retry-btn {
  color: #3370ff;
  font-weight: 500;
  margin-left: 4px;
}
.media-file-download {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
  max-width: 300px;
}
.media-file-download:hover {
  border-color: #4A5FF3;
  box-shadow: 0 2px 6px rgba(74, 95, 243, 0.15);
}
.media-file-type-badge {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.media-file-type-badge span {
  font-size: 10px;
  font-weight: 700;
  color: #fff;
}
.media-file-name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 500;
  color: #1f2329;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.media-file-download i {
  font-size: 14px;
  color: #8f959e;
  flex-shrink: 0;
}
.media-file-download:hover i {
  color: #4A5FF3;
}
</style>
